import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { resolveCrmProfileUsername } from '@/shared/auth/auth.reducer';
import { hasAnyAuthority } from '@/shared/auth/has-any-authority';
import { APP_FEATURE_GUARDS } from '@/shared/constants/app-permissions';
import { mapAgentWorkloadRows, mapDepartmentWorkloadRows, } from '../model/caseAssignment.model';
import { mapCrmDepartmentReference, resolveUserDepartmentSupervisorUsername, resolveDepartmentHeadUsername } from '../utils/supervisor-options';
import { appendCriteriaContains, appendCriteriaEquals, appendPageable, readPagedRows, } from '../utils/spring-api';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const initialState = {
    loading: false,
    errorMessage: null,
    cases: [],
    totalItems: 0,
    summary: null,
    summaryLoading: false,
    assignments: [],
    assignmentsLoading: false,
    reference: null,
    referenceLoading: false,
    updating: false,
    updateSuccess: false,
};
const baseUrl = API_ROUTES.caseAssignments;
const kasesUrl = API_ROUTES.kases;
function assignmentListQuery({ page, size, sort, kaseId, status, active, }) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaEquals(q, 'kaseId', kaseId);
    appendCriteriaEquals(q, 'status', status);
    appendCriteriaEquals(q, 'active', active);
    return q.toString();
}
function kaseListQuery({ page, size, sort, phone, caseNumber, status, assignmentFilter, assignedAgentUsername, }) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaContains(q, 'phoneNumber', phone);
    appendCriteriaContains(q, 'caseNumber', caseNumber);
    if (assignedAgentUsername) {
        appendCriteriaEquals(q, 'followUp', true);
        appendCriteriaEquals(q, 'assignedAgentUsername', assignedAgentUsername);
    }
    else {
        appendCriteriaEquals(q, 'status', status);
        if (assignmentFilter && assignmentFilter !== 'ALL') {
            appendCriteriaEquals(q, 'assignmentFilter', assignmentFilter);
        }
    }
    return q.toString();
}
function canViewAllCaseAssignments(state) {
    return hasAnyAuthority(state.auth.session?.permissions, APP_FEATURE_GUARDS.caseTransferSupervisor);
}
export const getCaseAssignmentSummary = createAsyncThunk(`${baseUrl}/summary`, async () => {
    const [openRes, workloadRes] = await Promise.all([
        axios.get(`${kasesUrl}/count?status.equals=OPEN`),
        axios.get(`${baseUrl}/workload`),
    ]);
    const workload = workloadRes.data;
    return {
        data: {
            totalUnassignedCases: typeof openRes.data === 'number' ? openRes.data : 0,
            departmentWorkload: mapDepartmentWorkloadRows(workload.departments),
            agentWorkload: mapAgentWorkloadRows(workload.agents),
        },
    };
}, { serializeError: serializeAxiosError });
export const getCaseAssignmentCases = createAsyncThunk(`${baseUrl}/cases`, async (params, { getState }) => {
    const state = getState();
    let assignedAgentUsername;
    if (!canViewAllCaseAssignments(state)) {
        assignedAgentUsername =
            resolveCrmProfileUsername(state.auth.crmProfile) ?? state.auth.session?.username;
        if (!assignedAgentUsername) {
            return { data: [], headers: { 'x-total-count': '0' } };
        }
    }
    const res = await axios.get(`${kasesUrl}?${kaseListQuery({ ...params, assignedAgentUsername })}`);
    const { rows, total } = readPagedRows(res.data, res.headers);
    return { data: rows, headers: { 'x-total-count': String(total) } };
}, { serializeError: serializeAxiosError });
export const getCaseAssignmentsForCase = createAsyncThunk('caseAssignment/fetch_assignments', async (caseId) => axios.get(`${baseUrl}?${assignmentListQuery({ kaseId: caseId, page: 0, size: 1000, active: true })}`), { serializeError: serializeAxiosError });
export const getCaseAssignmentReference = createAsyncThunk('caseAssignment/fetch_reference', async () => {
    const [usersRes, deptRes] = await Promise.all([
        axios.get(`${API_ROUTES.users}?page=0&size=1000&sort=id,desc`),
        axios.get(`${API_ROUTES.crmDepartments}?page=0&size=1000&sort=id,desc`),
    ]);
    const users = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.content ?? [];
    const departmentsRaw = Array.isArray(deptRes.data) ? deptRes.data : deptRes.data?.content ?? [];
    return {
        data: {
            departments: departmentsRaw.map((d) => ({
                ...mapCrmDepartmentReference(d),
                activeMembersCount: d.activeMembersCount,
                currentCaseCount: d.currentCaseCount,
            })),
            agents: users.map((u) => ({
                id: String(u.id ?? u.login ?? u.username ?? ''),
                name: u.fullName ??
                    ([u.firstName, u.lastName].filter(Boolean).join(' ') ||
                        (u.login ?? u.username ?? '')),
                username: u.login ?? u.username,
                position: u.position,
                currentCaseCount: u.currentCaseCount,
            })),
        },
    };
}, { serializeError: serializeAxiosError });
export const assignCaseToTarget = createAsyncThunk('caseAssignment/assign', async (payload) => axios.post(baseUrl, {
    kaseId: payload.caseId,
    assignedDepartmentId: payload.assignmentType === 0 ? payload.departmentId : undefined,
    assignedUserUsername: payload.assignmentType === 1 ? payload.agentUsername : undefined,
    role: payload.role,
    assignmentNotes: payload.notes,
    status: 'ASSIGNED',
}), { serializeError: serializeAxiosError });
export const updateCaseAssignmentRole = createAsyncThunk('caseAssignment/update_role', async ({ assignmentId, caseId, role, }) => axios.patch(`${baseUrl}/${assignmentId}`, {
    id: Number(assignmentId),
    kaseId: Number(caseId),
    role,
}), { serializeError: serializeAxiosError });
export const removeCaseAssignment = createAsyncThunk('caseAssignment/remove', async ({ assignmentId }) => axios.delete(`${baseUrl}/${assignmentId}`), { serializeError: serializeAxiosError });
export const transferCase = createAsyncThunk('caseAssignment/transfer', async (payload, { getState }) => {
    const state = getState();
    const fromUsername = resolveCrmProfileUsername(state.auth.crmProfile) ?? state.auth.session?.username;
    const body = {
        reason: payload.transferReason,
        transferNotes: payload.transferNotes,
        requiresApproval: payload.requiresApproval ?? false,
    };
    if (fromUsername) {
        body.fromUsername = fromUsername;
    }
    if (payload.transferToAgentUsername) {
        body.toUsername = payload.transferToAgentUsername;
    }
    if (payload.transferToDepartmentId) {
        body.toDepartmentId = Number(payload.transferToDepartmentId);
        const headUsername = resolveDepartmentHeadUsername(payload.transferToDepartmentId, state.caseAssignmentPage.reference?.departments ?? []);
        if (headUsername) {
            body.toUsername = headUsername;
        }
    }
    if (payload.requiresApproval) {
        const approvedByUsername = payload.approvedByUsername ??
            resolveUserDepartmentSupervisorUsername(fromUsername, state.caseAssignmentPage.reference?.departments ?? []);
        if (approvedByUsername) {
            body.approvedByUsername = approvedByUsername;
        }
    }
    return axios.post(API_ROUTES.caseTransferWorkflow.request(payload.caseId), body);
}, { serializeError: serializeAxiosError });
export const CaseAssignmentPageSlice = createSlice({
    name: 'caseAssignmentPage',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
        clearAssignments(state) {
            state.assignments = [];
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getCaseAssignmentSummary.fulfilled, (state, action) => {
            state.summaryLoading = false;
            state.summary = action.payload.data;
        })
            .addCase(getCaseAssignmentCases.fulfilled, (state, action) => {
            state.loading = false;
            state.cases = action.payload.data;
            state.totalItems = parseInt(action.payload.headers['x-total-count'] || '0', 10);
        })
            .addCase(getCaseAssignmentsForCase.fulfilled, (state, action) => {
            state.assignmentsLoading = false;
            const { rows } = readPagedRows(action.payload.data, action.payload.headers);
            state.assignments = rows;
        })
            .addCase(getCaseAssignmentReference.fulfilled, (state, action) => {
            state.referenceLoading = false;
            state.reference = action.payload.data;
        })
            .addMatcher(isFulfilled(assignCaseToTarget, updateCaseAssignmentRole, removeCaseAssignment, transferCase), (state) => {
            state.updating = false;
            state.updateSuccess = true;
        })
            .addMatcher(isPending(getCaseAssignmentSummary), (state) => {
            state.summaryLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getCaseAssignmentCases), (state) => {
            state.loading = true;
            state.errorMessage = null;
            state.updateSuccess = false;
        })
            .addMatcher(isPending(getCaseAssignmentsForCase), (state) => {
            state.assignmentsLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getCaseAssignmentReference), (state) => {
            state.referenceLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(assignCaseToTarget, updateCaseAssignmentRole, removeCaseAssignment, transferCase), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isRejected(getCaseAssignmentSummary), (state, action) => {
            state.summaryLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getCaseAssignmentCases), (state, action) => {
            state.loading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getCaseAssignmentsForCase), (state, action) => {
            state.assignmentsLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getCaseAssignmentReference), (state, action) => {
            state.referenceLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(assignCaseToTarget, updateCaseAssignmentRole, removeCaseAssignment, transferCase), (state, action) => {
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset, clearAssignments } = CaseAssignmentPageSlice.actions;
export default CaseAssignmentPageSlice.reducer;
